import { ClearRounded, SaveOutlined } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { StyledIconButton } from '../../../../theme/CustomComponents';
import { CollectionTag, CollectionWithCreator } from '../../../../Types/dealGPT';
import { collectionTagsPathUrl } from '../../../../util/routesUrls';
import { useAuth } from '../../../Providers/AuthProvider';
import TagSummaryChips from '../TagSummaryChips';

interface CollectionFormProps {
  collection?: CollectionWithCreator;
  onSave: (name: string, description?: string, tags?: string[]) => void;
  onCancel: () => void;
}

const CollectionForm: React.FC<CollectionFormProps> = ({ collection, onSave, onCancel }) => {
  const { isAdmin } = useAuth();
  const [name, setName] = useState<string | undefined>(collection?.name);
  const [collectionTags, setCollectionTags] = useState<string[] | undefined>(collection?.tags);
  const [description, setDescription] = useState<string | undefined>(
    collection?.description ?? undefined
  );

  const { data: tags } = useFetchData<CollectionTag[]>({ urlPath: collectionTagsPathUrl() });

  const tagsOptions = useMemo(() => {
    const tagNames = (tags ?? []).map(({ tag }) => tag);

    // Only admin users can see the 'Verified' tag
    return isAdmin ? tagNames : tagNames.filter((tag) => tag !== 'Verified');
  }, [tags, isAdmin]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 }}>
      <Box sx={{ display: 'flex', flexGrow: 1, gap: 2 }}>
        <TextField
          size="small"
          variant="outlined"
          label="Collection Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          sx={{ width: '400px' }}
          autoFocus
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={tagsOptions}
          value={collectionTags}
          onChange={(_, newValue) => setCollectionTags(newValue)}
          componentsProps={{
            popper: {
              onClick: (e) => e.stopPropagation(),
            },
          }}
          renderTags={(value) => (
            <TagSummaryChips
              tags={value}
              onRemoveTag={(tag) => setCollectionTags((prev) => prev?.filter((t) => t !== tag))}
            />
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name="tagInput"
              variant="outlined"
              size="small"
              label="Tags"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;

                const inputValue = params.inputProps.value?.toString().toLowerCase() || '';
                const filtered = tagsOptions.filter((option) =>
                  option.toLowerCase().includes(inputValue)
                );
                if (filtered.length === 1 && !collectionTags?.includes(filtered[0])) {
                  e.preventDefault();
                  setCollectionTags((prev) => [...(prev ?? []), filtered[0]]);
                }
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  // This targets the icons inside the input field
                  '& .MuiAutocomplete-endAdornment': {
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: 8,
                    position: 'absolute',
                  },
                },
              }}
            />
          )}
          sx={{ width: '450px' }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Save" arrow>
            <StyledIconButton
              onClick={(e) => {
                e.stopPropagation();
                if (name) {
                  onSave(name, description, collectionTags);
                }
              }}
              disabled={!name}
            >
              <SaveOutlined />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Cancel" arrow>
            <StyledIconButton
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              <ClearRounded />
            </StyledIconButton>
          </Tooltip>
        </Box>
      </Box>
      <TextField
        size="small"
        variant="outlined"
        label="Collection Description"
        value={description}
        multiline
        onChange={(e) => setDescription(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        sx={{ width: '870px' }}
      />
    </Box>
  );
};

export default CollectionForm;
